import { useSelector } from 'react-redux';

import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { IntakeForm } from '~/redux/api/methods/ticket';
import * as selectors from '~/redux/selectors';

import { AddressWorkflowQuestion } from './AddressWorkflowQuestion';
import { BooleanWorkflowQuestion } from './BooleanWorkflowQuestion';
import { CheckboxRadioWorkflowQuestion } from './CheckboxRadioWorkflowQuestion';
import { CompanyWorkflowQuestion } from './CompanyWorkflowQuestion';
import { ContractTypeWorkflowQuestion } from './ContractTypeWorkflowQuestion';
import { DateWorkflowQuestion } from './DateWorkflowQuestion';
import { DepartmentWorkflowQuestion } from './DepartmentWorkflowQuestion';
import { FileWorkflowQuestion } from './FileWorkflowQuestion';
import { MonetaryValueWorkflowQuestion } from './MonetaryValueWorkflowQuestion';
import { NumberWorkflowQuestion } from './NumberWorkflowQuestion';
import { SelectWorkflowQuestion } from './SelectWorkflowQuestion';
import { SupplierWorkflowQuestion } from './SupplierWorkflowQuestion';
import { TextWorkflowQuestion } from './TextWorkflowQuestion';
import { TimePeriodWorkflowQuestion } from './TimePeriodWorkflowQuestion';
import { useGetCommonQuestionFieldProps } from './useGetCommonQuestionFieldProps';
import { UserWorkflowQuestion } from './UserWorkflowQuestion';
import { WellKnownDateWorkflowQuestion } from './WellKnownDateWorkflowQuestion';

type Props = {
  id: string;
  search?: string;
};

export const WorkflowQuestion = ({ id, search }: Props) => {
  const question = useSelector((intakeForm: IntakeForm) =>
    selectors.selectQuestionAndField(intakeForm, id),
  );
  const commonProps = useGetCommonQuestionFieldProps({ question, search });
  const {
    field: { type, isEnabled, wellKnownKey },
  } = question;

  if (
    !isEnabled ||
    [WorkflowFieldType.DateSigned, WorkflowFieldType.Signature].includes(type)
  ) {
    return null;
  }

  switch (type) {
    case WorkflowFieldType.Address:
      return <AddressWorkflowQuestion {...commonProps} question={question} />;
    case WorkflowFieldType.Boolean:
      return <BooleanWorkflowQuestion {...commonProps} question={question} />;
    case WorkflowFieldType.Checkbox:
    case WorkflowFieldType.Radio:
      return (
        <CheckboxRadioWorkflowQuestion
          {...commonProps}
          question={question}
          type={type}
        />
      );
    case WorkflowFieldType.Date:
      if (wellKnownKey === 'start_date' || wellKnownKey === 'end_date') {
        return (
          <WellKnownDateWorkflowQuestion {...commonProps} question={question} />
        );
      }
      return <DateWorkflowQuestion {...commonProps} question={question} />;
    case WorkflowFieldType.Department:
      return (
        <DepartmentWorkflowQuestion {...commonProps} question={question} />
      );
    case WorkflowFieldType.File:
      return <FileWorkflowQuestion {...commonProps} question={question} />;
    case WorkflowFieldType.Email:
    case WorkflowFieldType.LongResponse:
    case WorkflowFieldType.ShortResponse:
      return (
        <TextWorkflowQuestion
          {...commonProps}
          type={type}
          question={question}
        />
      );
    case WorkflowFieldType.Reference: {
      if (wellKnownKey === 'supplier') {
        return (
          <SupplierWorkflowQuestion {...commonProps} question={question} />
        );
      }
      if (wellKnownKey === 'company') {
        return <CompanyWorkflowQuestion {...commonProps} question={question} />;
      }
      if (wellKnownKey === 'contract_type') {
        return (
          <ContractTypeWorkflowQuestion {...commonProps} question={question} />
        );
      }
      return (
        <TextWorkflowQuestion
          {...commonProps}
          type={type}
          question={question}
        />
      );
    }
    case WorkflowFieldType.MonetaryValue:
      return (
        <MonetaryValueWorkflowQuestion {...commonProps} question={question} />
      );
    case WorkflowFieldType.MultiSelect:
    case WorkflowFieldType.SingleSelect:
      return (
        <SelectWorkflowQuestion
          {...commonProps}
          question={question}
          type={type}
        />
      );
    case WorkflowFieldType.Number:
    case WorkflowFieldType.Percentage:
      return (
        <NumberWorkflowQuestion
          {...commonProps}
          question={question}
          type={type}
        />
      );
    case WorkflowFieldType.User:
      return <UserWorkflowQuestion {...commonProps} question={question} />;
    case WorkflowFieldType.TimePeriod:
      return (
        <TimePeriodWorkflowQuestion {...commonProps} question={question} />
      );
    default:
      return null;
  }
};
